import { cx } from '@orn/shared-web/utils/cx';

interface IAssistantWelcomeHeaderProps {
  /**
   * The name the greeting addresses the viewer by — their non-blank
   * `preferredName` when set, else `firstName`. Resolved by the pane; this
   * component renders whatever it is handed.
   */
  greetingName: string;
  /**
   * Active-alert count frozen when the session opened, or `null` when no
   * number can honestly be stated — the count has not resolved, or the alert
   * surfaces are disabled for this viewer. `null` renders the greeting alone;
   * the greeting never waits on the count.
   */
  activeAlertCount: number | null;
  /** First-ever session: greet without the "back" a returning user gets. */
  isFirstSession?: boolean;
  className?: string;
}

/**
 * The line under the greeting. Speaks in the user's terms — what needs their
 * attention — never in a check's terms ("no new alerts") or as a raw zero.
 */
function countLine(activeAlertCount: number): string {
  if (activeAlertCount === 0) {
    return "You're all caught up — nothing needs your attention right now.";
  }
  if (activeAlertCount === 1) {
    return 'There is 1 item that needs your attention.';
  }
  return `There are ${activeAlertCount} items that need your attention.`;
}

/**
 * The deterministic session-opening header: a display-serif "Welcome back,
 * {greetingName}" and one line stating how many alerts wait for the user. Pure —
 * rendered by the transcript for a `'welcome'` row, whose own dated session
 * divider draws above this. No data fetching: the pane freezes the count when
 * the session opens and hands it down.
 */
const AssistantWelcomeHeader = ({
  greetingName,
  activeAlertCount,
  isFirstSession = false,
  className,
}: IAssistantWelcomeHeaderProps) => (
  <div className={cx('flex flex-col gap-4 py-2', className)}>
    <div className="text-primary font-arizona text-display-sm tracking-[-0.6px]">
      {isFirstSession ? `Welcome, ${greetingName}` : `Welcome back, ${greetingName}`}
    </div>
    {activeAlertCount !== null && <div className="text-primary text-md font-medium">{countLine(activeAlertCount)}</div>}
  </div>
);

export default AssistantWelcomeHeader;
